import React from 'react'
import { Helmet } from 'react-helmet'

import Layout from '../components/Layout'
import SectionBanner from '../components/SectionBanner'
import Video from '../components/Video'
import City from '../components/svg/City'
import {
  Row,
  Column,
  Circle,
  Text,
  Link,
  Box,
  HR,
  LI,
  Heading
} from '../components/styled'

import { colors } from '../components/styled/theme'

import plotly from '../static/projects/plotly.png'
import dashDAQ from '../static/projects/dash-daq.png'
import carbon from '../static/projects/carbon.png'
import alchemy from '../static/projects/alchemy.png'
import bitpayper from '../static/projects/bitpayper.png'
import movecounselors from '../static/projects/movecounselors.png'
import zeit from '../static/projects/zeit.png'
import ranger from '../static/projects/ranger.png'

import workStatic from '../static/hero/work-static.png'
import ocean from '../static/videos/ocean-2.mp4'
import grass from '../static/videos/grass.mp4'

const HeroDynamic = props => (
  <Column position="relative" height="100%" mt="26.75rem" {...props}>
    <Box
      position="absolute"
      left="3.5rem"
      width="6.5rem"
      height="5rem"
      bottom="19rem"
      border={2}
      css={{
        background: colors.orange
      }}
    >
      <Video
        width="14rem"
        opacity={0.3}
        mixBlendMode="darken"
        objectPosition="bottom"
        height="100%"
        src={grass}
        i={1}
      />
    </Box>
    <Box
      position="absolute"
      left="3.5rem"
      top="-8.75rem"
      width="7.5rem"
      height="6rem"
      border={2}
      css={{
        background: colors.yellow
      }}
    >
      <Video
        opacity={0.3}
        width="100%"
        height="5.9rem"
        mixBlendMode="luminosity"
        objectPosition="left"
        src={ocean}
        i={4}
      />
    </Box>
    <Circle
      zIndex={-1}
      position="absolute"
      bg="green"
      top="-4rem"
      left="6rem"
      size="30.5rem"
    />
    <City />
  </Column>
)

const HeroStatic = props => (
  <Column justifyContent="center" height="100%" {...props}>
    <img width="100%" src={workStatic} alt="Work page artwork" />
  </Column>
)

function ExternalLink({ children, ...props }) {
  return <Link.external {...props}>{children} ↗</Link.external>
}

export const Project = ({
  title,
  img,
  description,
  link,
  tags = [],
  ...props
}) => (
  <Column id={title} border={2} mb={8} p={5} pb="2.5rem" {...props}>
    <Row mb={6}>
      <img width="100%" height="100%" src={img} alt={title} />
    </Row>
    {/* flexWrap and pb on text is for mobile */}
    <Row flexWrap="wrap">
      <Column mb={[4, 4, 0]} width="40%" css={{ minWidth: '250px' }}>
        <Heading.h3>{title}</Heading.h3>
        <Text.detail mt={2} pb={3}>
          {tags.join(', ')}
        </Text.detail>
      </Column>
      <Column width={['100%', '100%', '58%']} css={{ minWidth: '250px' }}>
        <Text.p mb={6}>{description}</Text.p>
        <Box mt={2}>{link}</Box>
      </Column>
    </Row>
  </Column>
)

const PROJECTS = [
  {
    title: 'Integral Case Study',
    description: (
      <React.Fragment>
        We recently had the chance to help{' '}
        <Link.external href="https://zeit.co" fontSize={3}>
          ZEIT
        </Link.external>{' '}
        and{' '}
        <Link.external fontSize={3} href="https://integral.studio">
          Integral
        </Link.external>{' '}
        share their story. Check out the case study we researched, wrote,
        designed, and built to do so.
      </React.Fragment>
    ),
    tags: ['Design', 'React'],
    img: zeit,
    link: (
      <ExternalLink href="http://zeit.co/case-studies/integral">
        VISIT SITE
      </ExternalLink>
    )
  },
  {
    title: 'Dash Deployment Server',
    description:
      'Plotly needed: an easy way for data scientists to deploy and configure a host of different applications for their private cloud offering. We built them an enterprise web platform that makes app management a breeze.',
    tags: ['GraphQL', 'React', 'Apollo', 'UI Design'],
    img: plotly,
    link: (
      <Link.internal to="/case-studies/plotly/dds">
        VIEW CASE STUDY
      </Link.internal>
    )
  },
  {
    title: 'Carbon',
    description:
      'Carbon is the easiest way to create and share beautiful images of your source code. With 20,000+ stars on GitHub, Carbon has become the go-to code presentation tool for developers and companies alike.',
    tags: ['Open-Source', 'React', 'Next.js', 'Now', 'PWA'],
    img: carbon,
    link: (
      <Link.internal to="/case-studies/carbon">VIEW CASE STUDY</Link.internal>
    )
  },
  {
    title: 'Dash DAQ',
    description:
      'Whether monitoring lab experiments or controlling a space fleet, scientists need a simple way to manage data acquisition. We built Plotly a UI component toolkit to help make this process approachable for users with little to no web programming experience.',
    tags: ['React', 'Dash', 'Python'],
    img: dashDAQ,
    link: (
      <Link.internal to="/case-studies/plotly/dash-daq">
        VIEW CASE STUDY
      </Link.internal>
    )
  },
  {
    title: 'MoveCounselors',
    description:
      'Move: Counselors simplifies home moves by connecting users with the top-rated vendors and tracking expenses as they prep their move. We built their GraphQL API from scratch.',
    tags: ['GraphQL', 'Node.js', 'Now', 'React'],
    img: movecounselors,
    link: (
      <ExternalLink href="https://www.movecounselors.com">
        VISIT SITE
      </ExternalLink>
    )
  },
  {
    title: 'Ranger',
    description:
      'Ranger is a GitHub bot for maintainers. Automate common tasks like merging pull requests, closing issues, creating tags and more.',
    tags: ['Node.js', 'Bot'],
    img: ranger,
    link: <ExternalLink href="https://reporanger.com">VISIT SITE</ExternalLink>
  },
  {
    title: 'Bitpayper',
    description:
      'With the speed of innovation surrounding cryptocurrencies, keeping up can be overwhelming. Bitpayper cuts through the noise and delivers crypto-asset reports you can trust. We designed a stylized and intuitive report format as well a custom CMS for managing site content.',
    tags: ['Vue', 'React', 'Branding', 'CMS'],
    img: bitpayper,
    link: (
      <ExternalLink href="https://www.bitpayper.com">VISIT SITE</ExternalLink>
    )
  },
  {
    title: 'Alchemy',
    description:
      'Many of the best image conversion tools ignore user experience altogether. With Alchemy, we set out to create an open source tool to tackle this problem within a delightful, easy-to-use interface.',
    tags: ['Open-Source', 'Electron', 'React', 'Go'],
    img: alchemy,
    link: (
      <ExternalLink href="https://dawnlabs.io/alchemy">VISIT SITE</ExternalLink>
    )
  }
]

const Work = props => (
  <Layout location={props.location}>
    <Column width="100%" alignItems="center">
      <Helmet>
        <title>Work — Dawn Labs</title>
      </Helmet>
      <Row
        width="100%"
        flexDirection={['column-reverse', 'row']}
        justifyContent={['flex-end', 'space-between']}
        height={['unset', '48rem']}
        alignItems="center"
        pt={['4.5rem', 'unset']}
        pb={[7, 'unset']}
      >
        <Column
          justifyContent="center"
          width={['100%', '50%', '48%', '38%']}
          maxWidth={['unset', '340px', '360px', 'unset']}
        >
          <Heading.h1 mb={5}>Our Work</Heading.h1>
          <Text.subheader width={['100%', '90%']}>
            We&#39;re a full-service studio that helps organizations big and
            small turn their ideas into products.
          </Text.subheader>
        </Column>
        <HeroDynamic display={['none', 'none', 'flex']} />
        <HeroStatic
          width={['100%', '45%']}
          maxWidth={[200, 'unset']}
          mb={[7, 0]}
          display={['flex', 'flex', 'none']}
        />
      </Row>
      <HR />
      <SectionBanner color="black" title="Capabilities">
        Our team draws inspiration from a number of different disciplines, but
        our best work comes when we blur the boundaries between them. We’re one
        of the few studios who can take your product from idea all the way to
        production.
      </SectionBanner>
      <Column
        width="100%"
        justifyContent="flex-start"
        flexDirection={['column', 'row']}
        mt={6}
        mb={8}
      >
        <Column width={['100%', '25%']} mr={[0, 7, 8]} mb={[7, 0]}>
          <Text.p
            fontWeight="medium"
            pb={2}
            style={{ borderBottom: '1px dotted #8F8F8F' }}
            mb={3}
          >
            Design
          </Text.p>
          <ul>
            <LI>branding</LI>
            <LI>UI & UX</LI>
            <LI>design systems</LI>
            <LI>art direction</LI>
            <LI>ephemera</LI>
          </ul>
        </Column>
        <Column width={['100%', '25%']} mr={[0, 7, 8]} mb={[7, 0]}>
          <Text.p
            fontWeight="medium"
            pb={2}
            style={{ borderBottom: '1px dotted #8F8F8F' }}
            mb={3}
          >
            Engineering
          </Text.p>
          <ul>
            <LI>web, desktop & mobile applications</LI>
            <LI>APIs / backend systems</LI>
            <LI>developer tools and frameworks</LI>
            <LI>open-source projects</LI>
          </ul>
        </Column>
        <Column width={['100%', '25%']}>
          <Text.p
            fontWeight="medium"
            pb={2}
            style={{ borderBottom: '1px dotted #8F8F8F' }}
            mb={3}
          >
            Research
          </Text.p>
          <ul>
            <LI>prototype development</LI>
            <LI>user research</LI>
            <LI>concept development</LI>
            <LI>product strategy</LI>
          </ul>
        </Column>
      </Column>
      <HR />
      <SectionBanner title="Projects">
        From brand identities to full blown applications, we have experience
        building things for organizations of all sizes.
      </SectionBanner>
      <Column width={['100%', '100%', '90%']}>
        {PROJECTS.map(props => (
          <Project key={props.title} {...props} />
        ))}
      </Column>
    </Column>
  </Layout>
)

export default Work
